import clsx from "clsx";
import { LandingPageContent } from "./landing-page-content";
import { Navbar } from "@common/ui/navigation/navbar/navbar";
import { Button, ButtonProps } from "@common/ui/buttons/button";
import { MixedImage } from "@common/ui/images/mixed-image";
import { Footer } from "@common/ui/footer/footer";
import { Trans } from "@common/i18n/trans";
import { AdHost } from "@common/admin/ads/ad-host";
import { Link } from "react-router-dom";
import { createSvgIconFromTree } from "@common/icons/create-svg-icon";
import { MenuItemConfig } from "@common/core/settings/settings";
import { Fragment, useState } from "react";
import { DefaultMetaTags } from "@common/seo/default-meta-tags";
import { useSettings } from "@common/core/settings/use-settings";
import { LandingPageNewLinkForm } from "@app/landing/landing-page-new-link-form";
import { useAuth } from "@common/auth/use-auth";
import { LandingPageStats } from "@app/landing/landing-page-stats";
import { PricingTable } from "@common/billing/pricing-table/pricing-table";
import { BillingCycleRadio } from "@common/billing/pricing-table/billing-cycle-radio";
import { UpsellBillingCycle } from "@common/billing/pricing-table/find-best-price";
import { useProducts } from "@common/billing/pricing-table/use-products";

interface ContentProps {
  content: LandingPageContent;
}
export function LandingPage() {
  const settings = useSettings();
  const homepage = settings.homepage as { appearance: LandingPageContent };

  const showPricing =
    settings.links.homepage_pricing && settings.billing.enable;

  return (
    <Fragment>
      <DefaultMetaTags />
      <Fragment>
        <BottomCta content={homepage.appearance} />
      </Fragment>
    </Fragment>
  );
}

interface CtaButtonProps extends ButtonProps {
  item?: MenuItemConfig;
}
function CtaButton({ item, ...buttonProps }: CtaButtonProps) {
  if (!item?.label || !item?.action) return null;
  const Icon = item.icon ? createSvgIconFromTree(item.icon) : undefined;
  return (
    <Button
      elementType={item.type === "route" ? Link : "a"}
      href={item.action}
      to={item.action}
      startIcon={Icon ? <Icon /> : undefined}
      {...buttonProps}
    >
      <Trans message={item.label} />
    </Button>
  );
}

interface PricingSectionProps {
  content: LandingPageContent;
}
function PricingSection({ content }: PricingSectionProps) {
  const query = useProducts("landingPage");
  const [selectedCycle, setSelectedCycle] =
    useState<UpsellBillingCycle>("yearly");
  return (
    <div className="py-80 sm:py-128" id="pricing">
      <div className="mx-auto max-w-1280 px-24 lg:px-32">
        <div className="md:text-center">
          <h2
            className="font-display text-3xl tracking-tight sm:text-4xl"
            data-testid="pricingTitle"
          >
            <Trans message={content.pricingTitle} />
          </h2>
          <p className="mt-16 text-lg text-muted" data-testid="pricingSubtitle">
            <Trans message={content.pricingSubtitle} />
          </p>
        </div>
        <BillingCycleRadio
          products={query.data?.products}
          selectedCycle={selectedCycle}
          onChange={setSelectedCycle}
          className="my-50 flex justify-center"
          size="lg"
        />
        <PricingTable
          selectedCycle={selectedCycle}
          productLoader="landingPage"
        />
      </div>
    </div>
  );
}

function BottomCta({ content }: ContentProps) {
  return (
    <div
      className="relative overflow-hidden bg-[rgb(37,99,235)] py-90 text-white md:py-128"
      data-testid="footerImage"
    >
      <img
        src={content.footerImage}
        alt=""
        width="2347"
        height="1244"
        decoding="async"
        loading="lazy"
        className="absolute left-1/2 top-1/2 max-w-none -translate-x-1/2 -translate-y-1/2"
      />
      <div className="relative mx-auto max-w-1280 px-24 text-center sm:px-16 lg:px-32">
        <div className="mx-auto max-w-512 text-center">
          <h2
            className=" font-display text-3xl tracking-tight sm:text-4xl"
            data-testid="footerTitle"
          >
            <Trans message={content.footerTitle} />
          </h2>
          {content.footerSubtitle && (
            <p
              className="mt-16 text-lg tracking-tight"
              data-testid="footerSubtitle"
            >
              <Trans message={content.footerSubtitle} />
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
